<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { usePublicNavItems, usePublicUserMenuItems } from '@/composables/usePublicNavItems';
import { useLocaleSwitch, type SupportedLocale } from '@/composables/useLocaleSwitch';
import BaseButton from '@/lib/ui/components/BaseButton.vue';

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'login'): void;
}>();

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const navItems = usePublicNavItems({ videos: true });
const userMenuItems = usePublicUserMenuItems();
const { switchLocale } = useLocaleSwitch();

const locales: SupportedLocale[] = ['es', 'en', 'pt'];
const DESKTOP_QUERY = '(min-width: 768px)';

let previousOverflow = '';
let desktopMedia: MediaQueryList | null = null;

const close = () => emit('close');

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') close();
};

const handleDesktopChange = (event: MediaQueryListEvent) => {
  if (event.matches) close();
};

const handleLogout = async () => {
  await authStore.logout();
  await router.push('/');
  close();
};

watch(() => route.fullPath, close);

// Client-only: this component renders only while open (v-if), and onMounted never runs during SSR.
onMounted(() => {
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  document.addEventListener('keydown', handleKeydown);
  desktopMedia = typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY) : null;
  desktopMedia?.addEventListener('change', handleDesktopChange);
});

onUnmounted(() => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = previousOverflow;
  document.removeEventListener('keydown', handleKeydown);
  desktopMedia?.removeEventListener('change', handleDesktopChange);
});
</script>

<template>
  <nav
    id="public-mobile-menu"
    data-testid="public-mobile-menu"
    :aria-label="t('nav.menu')"
    class="md:hidden fixed inset-x-0 bottom-0 top-[65px] z-30 flex flex-col overflow-y-auto bg-noir-950 px-6 py-7"
  >
    <ul class="flex shrink-0 flex-col gap-1">
      <li v-for="item in navItems" :key="item.to">
        <RouterLink
          :to="item.to"
          class="block py-2 font-display text-[28px] font-semibold leading-tight text-cream-100 transition-colors hover:text-gold-400"
          :active-class="item.to === '/' ? '' : 'text-gold-400'"
          exact-active-class="text-gold-400"
          @click="close"
        >
          {{ item.label }}
        </RouterLink>
      </li>
    </ul>

    <hr class="my-6 shrink-0 border-0 h-px bg-noir-700/60" />

    <div class="shrink-0">
      <p class="mb-3 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-noir-300">
        {{ t('locale.current') }}
      </p>
      <div class="flex gap-2">
        <button
          v-for="code in locales"
          :key="code"
          type="button"
          data-testid="mobile-menu-locale"
          :aria-pressed="locale === code"
          :aria-label="t(`locale.${code}`)"
          class="rounded-full border px-3.5 py-2 font-sans text-sm font-medium transition-colors"
          :class="locale === code ? 'border-gold-400 text-gold-300' : 'border-noir-700 text-noir-300 hover:text-gold-400'"
          @click="switchLocale(code)"
        >
          {{ code.toUpperCase() }}
        </button>
      </div>
    </div>

    <template v-if="authStore.isAuthenticated">
      <hr class="my-6 shrink-0 border-0 h-px bg-noir-700/60" />

      <div class="shrink-0" data-testid="mobile-menu-user">
        <div class="mb-2 flex items-center gap-3">
          <span class="h-10 w-10 rounded-full bg-noir-700" aria-hidden="true"></span>
          <span class="font-sans text-base font-medium text-cream-100">{{ authStore.user?.name }}</span>
        </div>
        <ul class="flex flex-col">
          <li v-for="item in userMenuItems" :key="item.key">
            <RouterLink
              :to="item.to"
              class="block py-3 font-sans text-base font-medium text-noir-300 transition-colors hover:text-gold-400"
              @click="close"
            >
              {{ t(item.key) }}
            </RouterLink>
          </li>
          <li>
            <button
              type="button"
              data-testid="mobile-menu-logout"
              class="block w-full py-3 text-left font-sans text-base font-medium text-noir-300 transition-colors hover:text-gold-400"
              @click="handleLogout"
            >
              {{ t('nav.logout') }}
            </button>
          </li>
        </ul>
      </div>
    </template>

    <BaseButton
      v-else
      variant="primary"
      class="mt-auto w-full shrink-0"
      data-testid="mobile-menu-login"
      @click="emit('login')"
    >
      {{ t('nav.login') }}
    </BaseButton>
  </nav>
</template>
